import { useEffect, useRef } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { identifyUser, resetAnalytics, setImpersonated } from '@tagadapay/analytics';
import { isStaff } from '@tagadapay/analytics/registry';

import { useSessionClaims } from '@/hooks/use-session-claims';
import { useOrgSlug } from '@/hooks/use-org-slug';

export interface PersonPropertiesInput {
  email: string | undefined;
  name: string | undefined;
  tgdRoles: string[];
  /** The user's own partner scope (`whitelabelOwnership`); null = global Tagada staff. */
  whitelabelScope: string | null;
  orgSlug: string | null;
  activeOrgWhitelabel: string | null;
}

/** The `$set` person properties sent with `identify`. */
export function personProperties(input: PersonPropertiesInput): Record<string, unknown> {
  return {
    email: input.email,
    name: input.name,
    tgd_roles: input.tgdRoles,
    // Any Tagada role at all means internal staff. Enumerating a subset would silently leak
    // payment_ops / crmAdmin / tagadapaymentAdmin into the merchant funnel as if they were
    // customers — the opposite of what this property exists for.
    is_tagada_admin: input.tgdRoles.length > 0,
    // Shared definition with V3 and the server: a partner's own team (role + partner scope)
    // is platform usage, not staff. The test-account filter reads this one.
    is_staff: isStaff(input.tgdRoles, input.whitelabelScope),
    org_slug: input.orgSlug ?? undefined,
    whitelabel: input.activeOrgWhitelabel ?? undefined,
  };
}

export interface AuthSnapshot {
  isLoaded: boolean;
  isImpersonating: boolean;
  isSignedIn: boolean;
  /** This tab has already seen an actor. */
  sawActor: boolean;
}

/**
 * What the impersonation flag becomes for a Clerk state: `true` under an actor,
 * `false` once Clerk has loaded without one (a signed-out visitor included,
 * unless this tab has seen an actor: the end of an impersonation stays silent
 * until a normal sign-in), `null` to leave it as it is.
 */
export function impersonationFlag(auth: AuthSnapshot): boolean | null {
  if (!auth.isLoaded) return null;
  if (auth.isImpersonating) return true;
  return auth.isSignedIn || !auth.sawActor ? false : null;
}

/**
 * Identifies the current merchant so the Guided Setup funnel is attributed to
 * a real user rather than a fragmented anonymous session. Idempotent and a
 * no-op when analytics is disabled, so this is safe to mount unconditionally.
 *
 * `is_tagada_admin` / `is_staff` are what let internal staff be excluded from
 * the funnel numbers. Org / white-label context is carried as PERSON
 * properties rather than PostHog groups, because group analytics is a paid
 * add-on the team deliberately does not use.
 *
 * An impersonated session (Clerk `actor`, "Sign in as user") is never
 * tracked: no identify, and the analytics package drops its events. With the
 * `identity` profile nothing leaves before Clerk has loaded (`awaitAuth`):
 * Clerk loaded without an `actor` releases it, signed in or not.
 */
export function useAnalyticsIdentity(): void {
  const { userId, actor, isSignedIn, isLoaded } = useAuth();
  const { user } = useUser();
  const { tgdRoles, whitelabelScope, activeOrgWhitelabel } = useSessionClaims();
  const { activeOrgSlug } = useOrgSlug();
  const isImpersonating = !!actor;

  // Declared before the identify effect so the flag is set before it runs. The flag is
  // in-memory only: it clears once a normal session is back, and a reload starts clean.
  const sawActor = useRef(false);
  useEffect(() => {
    const flag = impersonationFlag({
      isLoaded: Boolean(isLoaded),
      isImpersonating,
      isSignedIn: Boolean(isSignedIn),
      sawActor: sawActor.current,
    });
    if (flag === true) sawActor.current = true;
    if (flag !== null) setImpersonated(flag);
  }, [isLoaded, isImpersonating, isSignedIn]);

  // Keyed on the org too, not just the user: Clerk resolves the active organization after the
  // user, and merchants switch orgs without remounting. Keying on `userId` alone would freeze
  // whichever org context happened to be loaded first — usually none — leaving every person
  // profile without `org_slug` and making partner merchants unfilterable.
  const identifiedRef = useRef<string | null>(null);
  useEffect(() => {
    // Sign-out, session expiry and the cross-origin sign-out that lands here from the dashboard
    // all surface as `userId` going null. Clearing the identity here — rather than only in the
    // sign-out button handlers — is what stops the next merchant on a shared machine inheriting
    // the previous one's profile, and lets the same user be re-identified when they sign back in.
    if (!userId) {
      if (identifiedRef.current) {
        identifiedRef.current = null;
        resetAnalytics();
      }
      return;
    }
    // Never identify as the impersonated merchant; leaving the ref untouched lets the next
    // normal session identify.
    if (isImpersonating) return;
    // Wait for the Clerk user object so the $set person properties are
    // populated on the very first identify (roles/emails arrive
    // asynchronously; identifying early would write a half-empty person
    // profile that never gets corrected).
    const identityKey = `${userId}:${activeOrgSlug ?? ''}`;
    if (!user || identifiedRef.current === identityKey) return;
    identifiedRef.current = identityKey;
    identifyUser(
      userId,
      personProperties({
        email: user.primaryEmailAddress?.emailAddress,
        name: user.fullName ?? undefined,
        tgdRoles,
        whitelabelScope,
        orgSlug: activeOrgSlug ?? null,
        activeOrgWhitelabel,
      }),
    );
  }, [userId, user, isImpersonating, tgdRoles, whitelabelScope, activeOrgSlug, activeOrgWhitelabel]);
}
